import React, { useState } from 'react';
import AsyncSelect from 'react-select/async';
import { TripInfo } from '../../types/Quotation';
import { LocationOption } from '../../types/Location';
import { useItinerary } from '../../hooks/useItinerary';
import { Row, Label, Input, Section } from './UIPrimitives';
import { FlightTable } from '../QuotationBuilder/FlightTable';
import HotelTable from '../QuotationBuilder/HotelTable';
import TransferTable from '../QuotationBuilder/TransferTable';
import ActivityTable from '../QuotationBuilder/ActivityTable';
import TravellerSelector from '../Common/TravellerSelector';
import { ModalCallbacks } from '../QuotationBuilder/ModalManager';
import { loadAirportLocations, loadCityLocations } from '../../api/Location';
// Transfer modal is managed by ModalManager via openTransferModal

function PackageTripForm({
  tripInfo,
  handleTripInfoChange,
  itinerary,
  onChangeDayTitle,
  openFlightModal,
  openHotelModal,
  openActivityModal,
  openTransferModal,
  removeActivity,
  removeFlight,
  removeHotel,
  removeTransfer,
  onHotelImageSelect,
}: {
  tripInfo: TripInfo;
  handleTripInfoChange: (e: React.ChangeEvent<HTMLInputElement> | { name: string; value: any }) => void;
  itinerary: ReturnType<typeof useItinerary>[0];
  onChangeDayTitle: (dayIndex: number, title: string) => void;
  openFlightModal: ModalCallbacks['openFlightModal'];
  openHotelModal: ModalCallbacks['openHotelModal'];
  openActivityModal: ModalCallbacks['openActivityModal'];
  openTransferModal: ModalCallbacks['openTransferModal'];
  removeActivity: ModalCallbacks['removeActivity'];
  removeFlight?: ModalCallbacks['removeFlight'];
  removeHotel?: ModalCallbacks['removeHotel'];
  removeTransfer?: ModalCallbacks['removeTransfer'];
  onHotelImageSelect?: (dayIndex: number, hotelIndex: number, file: File | null) => void;
}) {
  const update = (name: string, value: any) => handleTripInfoChange({ name, value });

  const [placeDraft, setPlaceDraft] = useState('');
  const [nightsDraft, setNightsDraft] = useState<number>(1);
  const addPlace = () => {
    const label = placeDraft.trim();
    if (!label) return;
    const next = [...tripInfo.places, { label, value: label, nights: Math.max(1, Number(nightsDraft || 1)) }];
    update('places', next);
    setPlaceDraft('');
    setNightsDraft(1);
  };
  const removePlace = (idx: number) => update('places', tripInfo.places.filter((_, i) => i !== idx));
  const setPlaceField = (idx: number, field: 'label' | 'value' | 'nights', value: any) => {
    const next = tripInfo.places.map((p, i) => (i === idx ? { ...p, [field]: field === 'nights' ? Number(value || 0) : value } : p));
    update('places', next);
  };

  // Inclusions are now managed in the Media & other information section

  const loadAirportOptions = async (inputValue: string): Promise<LocationOption[]> => {
    try {
      const result = await loadAirportLocations(inputValue);
      return result || [];
    } catch (error) {
      return [];
    }
  };

  const loadCityOptions = async (inputValue: string): Promise<LocationOption[]> => {
    try {
      const result = await loadCityLocations(inputValue);
      return result || [];
    } catch (error) {
      return [];
    }
  };

  // Transfer modal is opened via ModalManager; no local state here

  return (
    <>
      <Section title="Trip Information">
        <Row>
          <div>
            <Label required>Trip title</Label>
            <Input name="title" value={tripInfo.title} onChange={handleTripInfoChange} placeholder="e.g., Bali Bliss Getaway" />
          </div>
          <div>
            <Label>Trip subtitle</Label>
            <Input name="subtitle" value={tripInfo.subtitle} onChange={handleTripInfoChange} placeholder="e.g., Brief description" />
          </div>
        </Row>

        <Row>
          <div style={{ flex: 1 }}>
            <Label>Trip overview</Label>
            <Input name="tripOverview" value={tripInfo.tripOverview || ''} onChange={handleTripInfoChange} placeholder="Short overview of the trip" />
          </div>
        </Row>

        <Row>
          <div>
            <Label>From city</Label>
            <AsyncSelect<LocationOption, false>
              cacheOptions
              defaultOptions
              loadOptions={loadAirportOptions}
              getOptionLabel={o => o.label}
              getOptionValue={o => o.value}
              onChange={(opt) => {
                update('from', opt ? { label: opt.label, value: opt.value } : undefined);
              }}
              value={
                tripInfo.from
                  ? { label: tripInfo.from.label, value: tripInfo.from.value, type: 'airport' }
                  : null
              }
              placeholder="Type a city or airport"
              styles={{
                control: (base) => ({
                  ...base,
                  minWidth: '200px'
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999
                })
              }}
              noOptionsMessage={() => "No airports found"}
              loadingMessage={() => "Loading airports..."}
              isClearable
              isSearchable
              menuPortalTarget={document.body}
            />
          </div>
          <div>
            <Label>Start date</Label>
            <Input type="date" name="startDate" value={tripInfo.startDate} onChange={handleTripInfoChange} />
          </div>
        </Row>

        <Row>
          <div>
            <Label>Travellers</Label>
            <TravellerSelector value={tripInfo.travellers} onChange={(travellers) => update('travellers', travellers)} />
          </div>
          <div style={{ alignSelf: 'end' }}>
            <Label> </Label>
            <button type="button" className="package-editor-btn-ghost" onClick={() => openFlightModal(false)}>
              + Add flights
            </button>
          </div>
        </Row>

        <div className="package-editor-hr" />
        <div>
          <div className="package-editor-label">Places to cover</div>

          <div className="package-editor-row package-editor-row-3" style={{ display: 'flex', gap: 16, alignItems: 'end' }}>
            <div>
              <Label>City</Label>
              <AsyncSelect<LocationOption, false>
                cacheOptions
                defaultOptions
                loadOptions={loadCityOptions}
                getOptionLabel={o => o.label}
                getOptionValue={o => o.value}
                onChange={(opt) => {
                  if (opt) {
                    setPlaceDraft(opt.label);
                  }
                }}
                value={
                  placeDraft
                    ? { label: placeDraft, value: placeDraft, type: 'city' }
                    : null
                }
                placeholder="Type a city name"
                styles={{
                  control: (base) => ({
                    ...base,
                    minWidth: '200px'
                  }),
                  menu: (base) => ({
                    ...base,
                    zIndex: 9999
                  })
                }}
                noOptionsMessage={() => "No cities found"}
                loadingMessage={() => "Loading cities..."}
                menuPortalTarget={document.body}
              />
            </div>
            <div>
              <Label>Nights</Label>
              <Input
                type="number"
                min={1}
                value={nightsDraft}
                onChange={(e) => setNightsDraft(Number(e.target.value || 0))}
              />
            </div>
            <div>
              <button type="button" className="package-editor-btn-primary" onClick={addPlace}>
               + Add place
              </button>
            </div>
          </div>
          {tripInfo.places.map((p, i) => (
            <div
              key={`${p.value}-${i}`}
              className="package-editor-row"
              style={{
                display: 'flex',
                gap: 16,
                alignItems: 'end',
                gridTemplateColumns: '1fr 100px 140px 40px',
              }}
            >
              <div>
                <AsyncSelect<LocationOption, false>
                  cacheOptions
                  defaultOptions
                  loadOptions={loadCityOptions}
                  getOptionLabel={o => o.label}
                  getOptionValue={o => o.value}
                  onChange={(opt) => {
                    if (opt) {
                      setPlaceField(i, 'label', opt.label);
                      setPlaceField(i, 'value', opt.value);
                    }
                  }}
                  value={
                    p.value
                      ? { label: p.label, value: p.value, type: 'city' }
                      : null
                  }
                  placeholder="Select city"
                  styles={{
                    control: (base) => ({
                      ...base,
                      minWidth: '200px'
                    })
                  }}
                  noOptionsMessage={() => "No cities found"}
                  loadingMessage={() => "Loading cities..."}
                />
              </div>
              <div>
                <Label> </Label>
                <Input
                  type="number"
                  min={1}
                  value={p.nights}
                  onChange={(e) => setPlaceField(i, 'nights', Number(e.target.value || 0))}
                />
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  className="package-editor-btn-ghost"
                  onClick={() => openHotelModal(i)}
                >
                  Select hotels
                </button>
                <button
                  type="button"
                  className="package-editor-btn-ghost"
                  onClick={() => removePlace(i)}
                  aria-label="remove"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}

          {/* Inclusions moved to Media & other information section */}
        </div>
      </Section>

      <Section title="Inclusions">
        <div className="package-editor-help">Summary of chosen flights, hotels, transfers and activities.</div>
        {/* Flights table */}
        <div style={{ marginTop: 12 }}>
          <h4 style={{ margin: '8px 0' }}>Flights</h4>
          <FlightTable itinerary={itinerary} hideActions={!removeFlight} onRemove={removeFlight}
          />
        </div>
        {/* Hotels table */}
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '8px 0' }}>Hotels</h4>
          <HotelTable
            itinerary={itinerary}
            hideActions={!removeHotel}
            onRemove={removeHotel}
            showRoomType={true}
          />
        </div>
        {/* Transfers table */}
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '8px 0' }}>Transfers</h4>
          <TransferTable
            itinerary={itinerary}
            hideActions={!removeTransfer}
            onRemove={removeTransfer}
          />
        </div>
        {/* Activities table */}
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: '8px 0' }}>Activities</h4>
          <ActivityTable
            itinerary={itinerary}
            hideActions={!removeActivity}
            onRemove={removeActivity}
          />
        </div>
      </Section>

      <Section title="Itinerary (auto-sized by nights)">
        <div className="package-editor-help">Use the modals above and places/nights to guide the daily plan.</div>
        
        <div className="itinerary-days">
          {itinerary.map((day, dayIndex) => (
            <div key={dayIndex} className="itinerary-day">
              <div className="day-header">
                <h3>Day {dayIndex + 1}</h3>
                <div className="day-place">{day.place?.label || 'Unknown'}</div>
              </div>

              <div style={{ marginTop: 8 }}>
                <Label>Day title</Label>
                <Input
                  value={day.title || ''}
                  onChange={(e) => onChangeDayTitle(dayIndex, e.target.value)}
                  placeholder="e.g., Arrival and City Tour"
                />
              </div>
              
              <div className="day-activities">
                <div className="activities-header">
                  <h4>Activities</h4>
                  <button 
                    type="button" 
                    className="package-editor-btn-ghost"
                    onClick={() => openActivityModal(dayIndex)}
                  >
                    + Add Activity
                  </button>
                </div>
                
                {day.activities && day.activities.length > 0 ? (
                  <div className="activities-list">
                    {day.activities.map((activity, activityIndex) => (
                      <div key={activityIndex} className="activity-item">
                        <div className="activity-info">
                          <div className="activity-name">{activity.name}</div>
                          <div className="activity-price">
                            {activity.from_price_paise ? `₹${Math.round(activity.from_price_paise / 100)}` : 'Price N/A'}
                          </div>
                        </div>
                        <button 
                          type="button" 
                          className="package-editor-btn-ghost"
                          onClick={() => removeActivity(dayIndex, activityIndex)}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-activities">No activities added yet</div>
                )}

                <div className="activities-header" style={{ marginTop: 16 }}>
                  <h4>Transfers</h4>
                  <button
                    type="button"
                    className="package-editor-btn-ghost"
                    onClick={() => openTransferModal(dayIndex)}
                  >
                    + Add Transfers
                  </button>
                </div>
                {day.transfers && day.transfers.length > 0 ? (
                  <div className="activities-list">
                    {day.transfers.map((t, transferIndex) => (
                      <div key={transferIndex} className="activity-item">
                        <div className="activity-info">
                          <div className="activity-name">{t.name} — {t.vehicle || ''}</div>
                          <div className="activity-price">{(t.currency === 'USD' ? '$' : t.currency === 'EUR' ? '€' : '₹')}{Number(t.price || 0).toLocaleString('en-IN')}</div>
                          <div className="activity-meta" style={{ fontSize: 12, color: '#555' }}>
                            {t.pickupDate} {t.pickupTime} • {t.pickupFromAirport
                              ? `${t.airportName || t.airportCode || '-'} → ${t.hotelName || t.hotelCode || '-'}`
                              : `${t.hotelName || t.hotelCode || '-'} → ${t.airportName || t.airportCode || '-'}`}
                          </div>
                        </div>
                        {removeTransfer && (
                          <button
                            type="button"
                            className="package-editor-btn-ghost"
                            onClick={() => removeTransfer(dayIndex, transferIndex)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-activities">No transfers added yet</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Transfer modal handled by ModalManager */}
    </>
  );
}

export default PackageTripForm;
